// 7-day match-rate sparkline. Height = call volume, colour = match rate
// band (emerald ≥40% · amber ≥15% · rose <15% · slate no-data).
//
// Extra canary: if the trailing 3+ days all have zero lookups, we flip the
// whole widget into a red warning state. Zero lookups for 3 days almost
// always means the Yabbit webhook stopped firing, extensions got
// unmapped, or the API creds silently went bad — worth surfacing before
// someone notices calls aren't popping up.
import React from 'react';
import { TrendingUp, AlertTriangle } from 'lucide-react';

const _tone = (rate, calls) => {
  if (!calls) return 'bg-white/10';
  if (rate == null) return 'bg-slate-500';
  if (rate >= 0.4) return 'bg-emerald-500';
  if (rate >= 0.15) return 'bg-amber-500';
  return 'bg-rose-500';
};

// Count the number of consecutive zero-call days at the END of the array
// (i.e. the current dry spell). Returns 0 if today had activity.
const _trailingDryDays = (trend) => {
  let n = 0;
  for (let i = trend.length - 1; i >= 0; i--) {
    if ((trend[i].calls || 0) === 0) n += 1;
    else break;
  }
  return n;
};

const MatchRateSpark = ({ trend }) => {
  if (!trend || trend.length === 0) return <div className="text-white/40 text-xs italic">No data yet.</div>;
  const maxCalls = Math.max(1, ...trend.map((t) => t.calls || 0));
  const totCalls = trend.reduce((s, t) => s + (t.calls || 0), 0);
  const totMatched = trend.reduce((s, t) => s + (t.matched || 0), 0);
  const overall = totCalls ? totMatched / totCalls : null;
  const dryDays = _trailingDryDays(trend);
  const isDry = dryDays >= 3;

  return (
    <div
      className={isDry ? 'rounded-lg border border-rose-500/40 bg-rose-500/5 p-2 -m-2' : ''}
      data-testid={isDry ? 'gymsales-match-trend-dry-warning' : undefined}
    >
      <div className="flex items-baseline justify-between mb-1">
        <div className={`text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1 ${isDry ? 'text-rose-300' : 'text-white/50'}`}>
          {isDry ? <AlertTriangle className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
          Match rate — 7 days
        </div>
        <div className="text-xs text-white/70">
          overall <b className="text-white">{overall == null ? '—' : `${Math.round(overall * 100)}%`}</b>
        </div>
      </div>
      <div className="flex items-end gap-1 h-14" data-testid="gymsales-match-trend">
        {trend.map((t) => {
          const heightPct = Math.max(6, Math.round(((t.calls || 0) / maxCalls) * 100));
          const label = t.calls
            ? `${t.day}: ${t.matched}/${t.calls} matched (${Math.round((t.match_rate || 0) * 100)}%)`
            : `${t.day}: no lookups`;
          return (
            <div key={t.day} className="flex-1 flex flex-col items-center gap-1" title={label}>
              <div
                className={`w-full rounded-t ${_tone(t.match_rate, t.calls)} transition-all`}
                style={{ height: `${heightPct}%` }}
              />
              <div className="text-[9px] text-white/40 font-mono">{t.day.slice(5)}</div>
            </div>
          );
        })}
      </div>
      {isDry && (
        <div className="mt-2 text-[11px] text-rose-200 leading-snug" data-testid="gymsales-match-trend-dry-message">
          <b>{dryDays} days without a single lookup.</b>{' '}
          Yabbit webhook, extension mappings, or API creds may have quietly stopped working. Check the endpoint probe matrix and recent calls table.
        </div>
      )}
    </div>
  );
};

export default MatchRateSpark;

